// Logica del mensaje de bloqueo de facturacion. Vive fuera del componente para
// poder probarla sin montarlo — y porque exportar funciones desde un archivo de
// componente rompe el fast refresh.

export interface BloqueoFactura {
  folio: string;
  fecha_pago: string;
  dias_habiles_vencidos?: number;
  /** La orden a la que pertenece, para poder llevar al proveedor a subir el complemento. */
  orden_id?: string;
}

/**
 * A donde mandar al proveedor desde el mensaje de bloqueo por complemento.
 *
 * El complemento se sube desde los pagos de una orden, asi que cuando todas las
 * facturas vencidas son de la misma orden se le deja en la pantalla exacta. Con
 * facturas de varias ordenes no hay una sola pantalla correcta y el listado es
 * lo mas cerca que se puede dejar.
 */
export const destinoDeFacturas = (facturas: BloqueoFactura[]): string => {
  const ordenes = new Set(facturas.map((f) => f.orden_id).filter(Boolean));
  const [unica] = [...ordenes];
  return ordenes.size === 1 && unica
    ? `/proveedores/ordenes/${unica}/pagos`
    : '/proveedores/ordenes';
};

/**
 * Objeto de error 422 que el backend devuelve al intentar subir una factura.
 *
 * Hay DOS convenciones vivas en el back, asi que se aceptan las dos en vez de
 * romperse con una: el bloqueo por documentos financieros manda `tipo` y
 * `estado` en espanol, y el del paquete mensual (SP-220, de Javier) manda
 * `type`, `status`, `month` y `year` en ingles. Si algun dia se unifican, esto
 * sigue funcionando.
 */
export interface BloqueoFacturacionError {
  tipo?: string;
  type?: string;
  facturas?: BloqueoFactura[];
  estado?: string;
  status?: string;
  mes?: string;
  month?: number;
  year?: number;
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/** El tipo de bloqueo, venga en ingles o en espanol. */
const tipoDeBloqueo = (error: BloqueoFacturacionError): string => {
  const crudo = error.type ?? error.tipo ?? '';
  return crudo === 'monthly_package' ? 'expediente_mensual' : crudo;
};

/** El estado del paquete, normalizado al espanol que ya usaban los mensajes. */
const estadoDelPaquete = (error: BloqueoFacturacionError): string => {
  const crudo = error.status ?? error.estado ?? '';
  const equivalencias: Record<string, string> = {
    pending: 'pendiente',
    in_review: 'en_revision',
    rejected: 'rechazado',
    approved: 'aprobado',
  };
  return equivalencias[crudo] ?? crudo;
};

/** "agosto de 2026" a partir de month/year, o el texto viejo si es lo que llega. */
const mesDelBloqueo = (error: BloqueoFacturacionError): string => {
  if (typeof error.month === 'number' && error.month >= 1 && error.month <= 12) {
    const nombre = MESES[error.month - 1];
    return error.year ? `${nombre} de ${error.year}` : nombre;
  }
  return error.mes || 'este mes';
};

interface BloqueoContent {
  body: string;
  facturas?: BloqueoFactura[];
  cta?: { label: string; to: string };
}

// Traduce el objeto de error al contenido a mostrar. Todo sale del error; el
// título es común ("No puedes subir facturas") para todos los casos.
// Exportado para poder probar la normalizacion de las dos convenciones del back
// sin montar el componente.
export const buildContent = (error: BloqueoFacturacionError): BloqueoContent => {
  const mes = mesDelBloqueo(error);

  switch (tipoDeBloqueo(error)) {
    case 'cfdi_complemento': {
      const facturas = error.facturas ?? [];
      return {
        body: `Tienes ${facturas.length} ${
          facturas.length === 1 ? 'factura pagada' : 'facturas pagadas'
        } sin CFDI de complemento de pago. Debes subir el complemento para continuar.`,
        facturas,
        cta: {
          label: 'Ir a mis facturas',
          to: destinoDeFacturas(facturas),
        },
      };
    }

    case 'expediente_mensual': {
      switch (estadoDelPaquete(error)) {
        case 'pendiente':
          return {
            body: `Tu paquete de documentos de ${mes} está pendiente de entrega. Súbelo completo para poder facturar este mes.`,
            cta: { label: 'Ir a Documentos del mes', to: '/proveedores/expediente' },
          };
        case 'en_revision':
          return {
            body: `Tu paquete de documentos de ${mes} está en revisión por Contraloría. Podrás facturar cuando sea aprobado.`,
            // Sin CTA: solo esperar.
          };
        case 'rechazado':
          return {
            body: `Tu paquete de documentos de ${mes} fue rechazado. Revisa los comentarios y vuelve a subirlo.`,
            cta: { label: 'Ver documentos rechazados', to: '/proveedores/expediente?estado=rechazado' },
          };
        default:
          // estado desconocido dentro de expediente_mensual → genérico
          return { body: 'No puedes subir facturas en este momento. Contacta a Contraloría.' };
      }
    }

    case 'docs_financieros':
      return {
        body: 'Contraloría ha solicitado documentación financiera adicional. Debes subir y esperar la aprobación para continuar facturando.',
        cta: { label: 'Ver documentos solicitados', to: '/proveedores/expediente?tipo=docs_financieros' },
      };

    default:
      // Tipo desconocido: mensaje genérico, sin CTA.
      return { body: 'No puedes subir facturas en este momento. Contacta a Contraloría.' };
  }
};

